const _state = Symbol('state');
const _subscribers = Symbol('subscribers');
const _hashCode = Symbol('hashCode');

const _propsDefinition = Symbol('propsDefinition');
const _defaultState = Symbol('defaultState');
const _metaProps = Symbol('metaProps');
const _props = Symbol('props');
const _volatileState = Symbol('volatileState');

export interface StateData {
  [key: string]: any;
}

export interface NotifyData {
  props: StateData;
  state: StateData;
}

class SingleSourceOfTruth {

  [_propsDefinition]: string[];

  [_state]: {
    [_defaultState]: StateData;
    [_metaProps]: StateData;
    [_props]: StateData;
    [_volatileState]: StateData;
  };

  [_hashCode]: string;

  [_subscribers]: Function[];

  constructor(hashCode: string, defaultState: StateData = {}, propsDefinition: string[] = []) {
    this[_propsDefinition] = propsDefinition;
    this[_state] = {
      [_defaultState]: Object.freeze(defaultState),
      [_metaProps]: {},
      [_props]: {},
      [_volatileState]: {},
    };
    this[_subscribers] = [];
    this[_hashCode] = hashCode;
  }

  get state(): StateData {
    const state = this[_state];
    const defaultState = state[_defaultState];
    const metaProps = state[_metaProps];
    const props = state[_props];
    const volatileState = state[_volatileState];

    return {
      ...defaultState,
      ...metaProps,
      ...props,
      ...volatileState,
    };
  }

  get defaultState(): StateData {
    return { ...this[_state][_defaultState] };
  }

  get metaProps(): StateData {
    return { ...this[_state][_metaProps] };
  }

  get props(): StateData {
    return { ...this[_state][_props] };
  }

  setMetaProps(props = {}): void {
    this[_state][_metaProps] = props;
  }

  setProps(props = {}): void {
    this[_state][_props] = props;
  }

  get volatileState(): StateData {
    return { ...this[_state][_volatileState] };
  }

  get hashCode(): string {
    return this[_hashCode];
  }

  cleanUp(): void {
    function clean(obj: StateData): void {
      Object.keys(obj).forEach(k => {
        if (obj[k] === undefined) {
          delete obj[k];
        }
      });
    }

    clean(this[_state][_props]);
    clean(this[_state][_volatileState]);
  }

  apply(data: StateData): NotifyData {
    const defaultState = this[_state][_defaultState];
    const metaProps = this[_state][_metaProps];
    const props = this[_state][_props];
    const volatileState = this[_state][_volatileState];
    const baseProps = { ...defaultState, ...metaProps, ...props };

    const propsDefinition = this[_propsDefinition];

    const newProps = {};
    const newState = {};

    Object.keys(data).forEach(key => {
      const isProp = propsDefinition.includes(key);

      const destinationObj = isProp ? props : volatileState;

      if (baseProps[key] !== data[key]) {
        destinationObj[key] = data[key];
      }

      const mapObj: StateData = isProp ? newProps : newState;
      mapObj[key] = data[key];
    });

    this.cleanUp();

    return { props: newProps, state: newState };
  }

  applyAndNotify(data: StateData): void {
    const result = this.apply(data);
    this.notify(result, {});
  }

  subscribe(cb: Function): () => void {
    this[_subscribers].push(cb);
    return () => {
      this[_subscribers] = this[_subscribers].filter((test) => test !== cb);
    };
  }

  notify(newState: object, lastState: object): void {
    this[_subscribers].forEach(cb => cb(newState, lastState));
  }

}

export default SingleSourceOfTruth;
